<template>
  <BasicModal
    title="リマインダー一覧"
    width="1080px"
    :canCancel="canCancel"
    :isShowModal="isShowModal"
    @cancel="onCancel"
  >
    <b-tabs v-model="activeTab" style="padding: 0px 24px 24px 24px; margin-bottom: 0px">
      <!-- 送信予定タブ -->
      <b-tab-item label="送信予定（30日間）">
        <div class="filter-section">
          <div class="field is-grouped">
            <p class="control">
              <input 
                v-model="searchPending.email" 
                class="input is-small" 
                type="text" 
                placeholder="メールアドレスで検索"
              >
            </p>
            <p class="control">
              <input 
                v-model="searchPending.subject" 
                class="input is-small" 
                type="text" 
                placeholder="件名・本文で検索"
              >
            </p>
            <p class="control">
              <button class="button is-small is-primary" @click="searchPendingReminders">
                <span class="mdi mdi-magnify"></span>検索
              </button>
            </p>
          </div>
        </div>

        <div class="table-container">
          <table class="table is-fullwidth is-striped">
            <thead>
              <tr>
                <th>送信予定日時</th>
                <th>送信先</th>
                <th>CC</th>
                <th>件名</th>
                <th>本文</th>
                <th>作成日時</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="reminder in filteredPendingReminders" :key="reminder.id">
                <td>{{ formatDateTime(reminder.send_datetime) }}</td>
                <td>{{ reminder.to_email }}</td>
                <td>{{ reminder.cc_email || '-' }}</td>
                <td>{{ reminder.subject }}</td>
                <td>
                  <div class="content-preview" v-html="truncateContent(reminder.content)"></div>
                </td>
                <td>{{ formatDateTime(reminder.created_at) }}</td>
                <td>
                  <button 
                    class="button is-small is-warning" 
                    @click="confirmStopReminder(reminder)"
                  >
                    <span class="mdi mdi-stop"></span>停止
                  </button>
                  <button 
                    class="button is-small is-danger" 
                    @click="confirmDeleteReminder(reminder)"
                    style="margin-left: 4px;"
                  >
                    <span class="mdi mdi-delete"></span>削除
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          
          <!-- ページネーション -->
          <nav class="pagination is-centered" role="navigation">
            <b-pagination
              v-model="pendingPage"
              :total="totalPendingReminders"
              :per-page="perPage"
              :range-before="3"
              :range-after="3"
              size="is-small"
              @change="loadPendingReminders"
            />
          </nav>
        </div>
      </b-tab-item>

      <!-- 送信済みタブ -->
      <b-tab-item label="送信済み（30日間）">
        <div class="filter-section">
          <div class="field is-grouped">
            <p class="control">
              <input 
                v-model="searchCompleted.email" 
                class="input is-small" 
                type="text" 
                placeholder="メールアドレスで検索"
              >
            </p>
            <p class="control">
              <input 
                v-model="searchCompleted.subject" 
                class="input is-small" 
                type="text" 
                placeholder="件名・本文で検索"
              >
            </p>
            <p class="control">
              <button class="button is-small is-primary" @click="searchCompletedReminders">
                <span class="mdi mdi-magnify"></span>検索
              </button>
            </p>
          </div>
        </div>

        <div class="table-container">
          <table class="table is-fullwidth is-striped">
            <thead>
              <tr>
                <th>送信日時</th>
                <th>送信先</th>
                <th>CC</th>
                <th>件名</th>
                <th>本文</th>
                <th>ステータス</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="reminder in filteredCompletedReminders" :key="reminder.id">
                <td>{{ formatDateTime(reminder.send_datetime) }}</td>
                <td>{{ reminder.to_email }}</td>
                <td>{{ reminder.cc_email || '-' }}</td>
                <td>{{ reminder.subject }}</td>
                <td>
                  <div class="content-preview" v-html="truncateContent(reminder.content)"></div>
                </td>
                <td>
                  <span class="tag" :class="reminder.error_log ? 'is-danger' : 'is-success'">
                    {{ reminder.error_log ? 'エラー' : '送信済み' }}
                  </span>
                </td>
                <td>
                  <button 
                    class="button is-small is-danger" 
                    @click="confirmDeleteReminder(reminder)"
                  >
                    <span class="mdi mdi-delete"></span>削除
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          
          <!-- ページネーション -->
          <nav class="pagination is-centered" role="navigation">
            <b-pagination
              v-model="completedPage"
              :total="totalCompletedReminders"
              :per-page="perPage"
              :range-before="3"
              :range-after="3"
              size="is-small"
              @change="loadCompletedReminders"
            />
          </nav>
        </div>
      </b-tab-item>
    </b-tabs>

    <!-- 確認ダイアログ -->
    <b-modal v-model="isConfirmModalActive" :width="320" scroll="keep">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ confirmTitle }}</p>
        </header>
        <section class="modal-card-body">
          <p>{{ confirmMessage }}</p>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="isConfirmModalActive = false">キャンセル</button>
          <button class="button is-danger" @click="executeConfirmAction">実行</button>
        </footer>
      </div>
    </b-modal>
  </BasicModal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import BasicModal from '@/components/Modals/BasicModal'
import moment from 'moment'

export default {
  name: 'ReminderListModal',
  components: {
    BasicModal
  },
  props: {
    isShowModal: {
      type: Boolean,
      required: true,
      default: false
    },
    canCancel: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      activeTab: 0,
      pendingPage: 1,
      completedPage: 1,
      perPage: 50,
      searchPending: {
        email: '',
        subject: ''
      },
      searchCompleted: {
        email: '',
        subject: ''
      },
      isConfirmModalActive: false,
      confirmTitle: '',
      confirmMessage: '',
      confirmAction: null,
      selectedReminder: null
    }
  },
  computed: {
    ...mapState('schedule', [
      'pendingReminders',
      'completedReminders',
      'totalPendingReminders',
      'totalCompletedReminders'
    ]),
    filteredPendingReminders() {
      return this.filterReminders(this.pendingReminders, this.searchPending)
    },
    filteredCompletedReminders() {
      return this.filterReminders(this.completedReminders, this.searchCompleted)
    }
  },
  mounted() {
    console.log('=== ReminderListModal mounted ===')
    this.loadPendingReminders()
    this.loadCompletedReminders()
  },
  methods: {
    ...mapActions('schedule', [
      'getReminderList',
      'stopReminder',
      'deleteReminder'
    ]),
    onCancel() {
      this.$emit('cancel')
    },
    async loadPendingReminders() {
      await this.getReminderList({
        status: 'pending',
        page: this.pendingPage,
        per_page: this.perPage
      })
    },
    async loadCompletedReminders() {
      await this.getReminderList({
        status: 'completed',
        page: this.completedPage,
        per_page: this.perPage
      })
    },
    filterReminders(reminders, search) {
      return reminders.filter(reminder => {
        const emailMatch = !search.email || 
          reminder.to_email.toLowerCase().includes(search.email.toLowerCase())
        const subjectMatch = !search.subject || 
          reminder.subject.toLowerCase().includes(search.subject.toLowerCase()) ||
          reminder.content.toLowerCase().includes(search.subject.toLowerCase())
        return emailMatch && subjectMatch
      })
    },
    searchPendingReminders() {
      this.pendingPage = 1
      this.loadPendingReminders()
    },
    searchCompletedReminders() {
      this.completedPage = 1
      this.loadCompletedReminders()
    },
    formatDateTime(datetime) {
      console.log('formatDateTime input:', datetime, 'type:', typeof datetime)
      if (!datetime) {
        return '未設定'
      }
      const m = moment(datetime)
      if (!m.isValid()) {
        return '日時エラー'
      }
      return m.format('YYYY/MM/DD HH:mm')
    },
    truncateContent(content) {
      const stripped = content.replace(/<[^>]*>/g, '')
      return stripped.length > 100 ? stripped.substring(0, 100) + '...' : stripped
    },
    confirmStopReminder(reminder) {
      this.selectedReminder = reminder
      this.confirmTitle = '送信停止確認'
      this.confirmMessage = `このリマインドメールの送信を停止しますか？\n送信先: ${reminder.to_email}\n件名: ${reminder.subject}`
      this.confirmAction = this.executeStopReminder
      this.isConfirmModalActive = true
    },
    confirmDeleteReminder(reminder) {
      this.selectedReminder = reminder
      this.confirmTitle = '削除確認'
      this.confirmMessage = `このリマインドメールを削除しますか？\n送信先: ${reminder.to_email}\n件名: ${reminder.subject}`
      this.confirmAction = this.executeDeleteReminder
      this.isConfirmModalActive = true
    },
    async executeConfirmAction() {
      if (this.confirmAction) {
        await this.confirmAction()
      }
      this.isConfirmModalActive = false
    },
    async executeStopReminder() {
      try {
        await this.stopReminder(this.selectedReminder.id)
        this.$buefy.toast.open({
          message: 'リマインドメールの送信を停止しました',
          type: 'is-success'
        })
        this.loadPendingReminders()
      } catch (error) {
        this.$buefy.toast.open({
          message: 'エラーが発生しました',
          type: 'is-danger'
        })
      }
    },
    async executeDeleteReminder() {
      try {
        await this.deleteReminder(this.selectedReminder.id)
        this.$buefy.toast.open({
          message: 'リマインドメールを削除しました',
          type: 'is-success'
        })
        if (this.activeTab === 0) {
          this.loadPendingReminders()
        } else {
          this.loadCompletedReminders()
        }
      } catch (error) {
        this.$buefy.toast.open({
          message: 'エラーが発生しました',
          type: 'is-danger'
        })
      }
    }
  }
}
</script>

<style scoped>
.filter-section {
  margin-bottom: 1rem;
}

.table-container {
  overflow-x: auto;
  margin-bottom: 1rem;
}

.content-preview {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.icon-margin {
  margin-right: 0.25rem;
}
</style>


// WEBPACK FOOTER //
// src/components/organisms/contents/ReminderListModal.vue